/* eslint-disable no-param-reassign */

import $ from 'jquery';
import _ from 'underscore';
import { include } from 'underscore.string';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { prompt as inlinePopupEditor } from '@bingads-webui/popup-editor';
import { OverridableToolbarView } from '@bingads-webui/toolbar';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { triggerScrollbarRepositionOnElementHeightChange } from '@bingads-webui-campaign-react/virtual-scrollbar-recalculate-trigger';
import { getGridName } from '@bingads-webui-campaign/inline-download-grids-metadata';
import { GridCustomizedPopupEditor } from '@bingads-webui-campaign/grid-customized-popup-editor';
import hotelNameColumnTemp from './hotel-name-column.pug';
import { conversionColumns } from './hotel-center-conversion-columns';
import { instrumentation, SubAccountModel } from '../hotel-center-shared';
import { bidMultiplierUtils } from '../hotel-center-utils';

const { crossPageSelectHeadRow } = globalContext.require('component/bingads-grid/plugin/cross-page-select-all/index');
const { bingadsGridColumn } = globalContext.require('component/bingads-grid/shared/column');
const config = globalContext.require('component/config/index');
const decimal = globalContext.require('component/humanize/decimal');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/hotel-center');
const i18nShared = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/hotel-center-shared');
const state = globalContext.require('component/state/index');
const { DownloadView } = globalContext.require('campaignui/feature/griddownload/view/adapter');
const s$i18n = globalContext.require('$/schema-i18n!hotel-center');
const tableContainerSelector = '.bingadsgrid.hotel-center-overview-grid-container';
const {
  Columns: ColumnsResource,
  SelectedColumns: SelectedColumnsResource,
} = globalContext.require('component/bingads-grid/service/column-chooser-preferences');
const pageContext = globalContext.require('PageContext');

const getColumnChooserConfig = ({
  attributesColumns,
  performanceColumns,
}) => ({
  categories: _.compact([{
    name: i18n.getString('Attributes'),
    options: _.map(attributesColumns, ({ name, title }) => ({
      key: name,
      name: title,
      selectOption: name === 'Name' ? 'locked' : 'optional',
    })),
  }, {
    name: i18n.getString('Performance'),
    options: _.map(performanceColumns, ({ name, title }) => ({
      key: name,
      name: title,
      selectOption: 'optional',
    })),
  }, {
    name: i18n.getString('Conversions'),
    options: _.map(conversionColumns('Metrics/'), ({ name, title }) => ({
      key: name,
      name: title,
      selectOption: 'optional',
    })),
  }]),
  selected: ['Id', 'Name', 'Clicks', 'Impressions', 'CTR', 'Spend', 'AverageCPC', 'AveragePosition'],
  locked: ['Id'],
  customToolbarIcon: 'ba-TripleColumnSolid',
  Columns: ColumnsResource,
  SelectedColumns: SelectedColumnsResource,
  i18n,
  isForVNext: true,
  isHotelCenterGrid: true,
});

export class HotelGridOptionsBase {
  constructor({
    view,
    gridName,
    dateFilterContainer,
    schema,
    entitySet,
    onBulkEditChangeBids,
    onBulkEditChangeMultipliersBids,
    onBulkEditIncreseOrDecreseBids,
    serverContext,
    preferenceService,
    p$daterange,
    filterService,
    viewport,
    customHeaderOffset,
  }) {
    this.i18n = i18n;
    this.filterService = filterService;
    this.gridName = gridName;
    this.view = view;
    this.schema = schema;
    this.p$daterange = p$daterange;
    const pageSizes = [20, 50, 100, 200];
    const permissions = pageContext.Permissions;
    const isHotelCenterDeprecation = permissions && permissions.dynamic.HotelCenterDeprecation;

    this.name = getGridName(schema.name, {
      CampaignId: state.get('subAccount_id'),
    });
    this.preferenceService = preferenceService;
    this.config = config;
    this.tableClasses = ['table', 'table-bordered'];
    this.scrolling = {
      virtualized: true,
      viewport: viewport || window,
      header: {
        type: 'sticky',
        offset: customHeaderOffset || (() => $('.navbar-fixed-top').height()),
      },
    };
    this.dataSource = {
      type: 'js-data',
      options: {
        entitySet,
        cacheResponse: false,
      },
      schema,
    };

    this.selectable = true;

    this.selection = {
      enabled: !isHotelCenterDeprecation,
    };

    this.attributesColumns = [bingadsGridColumn({
      name: 'Bid',
      title: 'HotelCenter_Overview_Grid_Bid_Label',
      locked: true,
      property: {
        get(subAccount) {
          return subAccount.Bid;
        },
        key: 'Bid/Amount',
      },
      editable: {
        enableEdit: () => !isHotelCenterDeprecation,
        prompt: options => this.bidPrompt(options, serverContext),
        i18n: this.i18n,
      },
      template: (local) => {
        let bidString = '';

        if (local.value && local.value['@odata.type']) {
          if (local.value['@odata.type'] === '#Model.FixedBid') {
            bidString = `${i18n.formatDecimalToFixed(local.value.Amount)} ${config.get('AccountCurrencyCode')}`;
          } else if (local.value['@odata.type'] === '#Model.PercentageBid' || local.value['@odata.type'] === '#Model.PayPerStayBid') {
            bidString = `${i18n.formatDecimalToFixed(local.value.Amount)} ${decimal.getFormat().percentSymbol}`;
          }
        }

        return bidString;
      },
      sortable: 'Bid/Amount',
      colClasses: ['col-Bid'],
      helpId: 'pop_BA_HotelAds_Bid',
      i18n: this.i18n,
    }), bingadsGridColumn({
      name: 'BidMultipliers',
      title: 'HotelCenter_Overview_Grid_Bid_Multipliers_Label',
      property: 'BidMultipliers',
      editable: {
        enableEdit: () => !isHotelCenterDeprecation,
        prompt: options => this.bidMultipliersPrompt(options, serverContext),
        i18n: this.i18n,
      },
      template: (local) => {
        if (local.value && !_.isEmpty(local.value)) {
          return bidMultiplierUtils.getMultipliersText(local.value, false);
        }
        return '-';
      },
      colClasses: ['col-Bid-Multipliers'],
      bodyClasses: ['col-Bid-Multipliers'],
      helpId: 'pop_BA_HotelAds_BidMultiplier',
      i18n: this.i18n,
    }), bingadsGridColumn({
      name: 'Name',
      sortable: 'Name',
      title: this.i18n.getString('HotelCenter_Overview_Grid_SubAccount_Name_Label'),
      type: 'string',
      property: {
        get(currentColumn) {
          return currentColumn;
        },
        key: 'Name',
      },
      template: (local) => {
        if (gridName !== 'HotelIdGrid') {
          return hotelNameColumnTemp({
            name: local.value.Name || ' ',
            gridName,
          });
        }
        return local.value.Name || ' ';
      },
      editable: {
        enableEdit: () => gridName === 'HotelSubAccountGrid' && !isHotelCenterDeprecation,
        prompt: options => this.namePrompt(options),
        i18n: this.i18n,
      },
      bodyClasses: ['col-Name'],
    })];

    this.performanceColumns = [bingadsGridColumn({
      name: 'Clicks',
      property: 'Metrics/Clicks',
      title: 'HotelCenter_Overview_Grid_Clicks_Label',
      type: 'number',
      format: 'integer',
      sortable: true,
      helpId: 'pop_BA_HotelAds_Clicks',
      i18n: this.i18n,
    }), bingadsGridColumn({
      name: 'Impressions',
      property: 'Metrics/Impressions',
      title: 'HotelCenter_Overview_Grid_Impression_Label',
      type: 'number',
      format: 'integer',
      sortable: true,
      helpId: 'pop_BA_HotelAds_Impressions',
      i18n: this.i18n,
    }), bingadsGridColumn({
      name: 'CTR',
      property: 'Metrics/CTR',
      title: 'HotelCenter_Overview_Grid_CTR_Label',
      type: 'number',
      format: 'percentage-value',
      sortable: true,
      helpId: 'pop_BA_HotelAds_CTR',
      i18n: this.i18n,
    }), bingadsGridColumn({
      name: 'Spend',
      property: 'Metrics/Spend',
      title: 'HotelCenter_Overview_Grid_Spend_Label',
      type: 'number',
      sortable: true,
      helpId: 'pop_BA_HotelAds_Spend',
      i18n: this.i18n,
    }), bingadsGridColumn({
      name: 'SpendUSD',
      property: 'Metrics/SpendUSD',
      title: 'HotelCenter_Overview_Grid_Spend_USD_Label',
      type: 'number',
      sortable: true,
      helpId: 'pop_BA_HotelAds_Spend',
      i18n: this.i18n,
    }), bingadsGridColumn({
      name: 'AverageCPC',
      property: 'Metrics/AverageCPC',
      filed: 'AverageCPC',
      sortable: true,
      type: 'number',
      title: this.i18n.getString('HotelCenter_Overview_Grid_AverageCPC_Label'),
    }), bingadsGridColumn({
      name: 'AverageCPCUSD',
      property: 'Metrics/AverageCPCUSD',
      filed: 'AverageCPCUSD',
      sortable: true,
      type: 'number',
      title: this.i18n.getString('HotelCenter_Overview_Grid_AverageCPC_USD_Label'),
    })];

    if (!permissions.IsAvgPositionDeprecationPhase3Enabled) {
      this.performanceColumns.push(bingadsGridColumn({
        name: 'AveragePosition',
        property: 'Metrics/AveragePosition',
        sortable: true,
        type: 'number',
        title: this.i18n.getString('HotelCenter_Overview_Grid_AveragePosition_Label'),
      }));
    }

    this.columns = this.attributesColumns.concat(this.performanceColumns);

    // add subaccount name column for hotel/hotelgroup grid while all accounts is selected
    const enableColSubAccountName = gridName !== 'HotelSubAccountGrid' && !state.get('subAccount_id');

    if (enableColSubAccountName) {
      this.columns.push(bingadsGridColumn({
        name: 'SubAccountName',
        sortable: gridName !== 'HotelGroupGrid' && 'SubAccountName',
        title: this.i18n.getString('HotelCenter_Overview_Grid_Shared_SubAccount_Name_Label'),
        type: 'string',
        bodyClasses: ['col-Name'],
        template: (local) => {
          if (!isHotelCenterDeprecation) {
            return hotelNameColumnTemp({
              name: local.value,
              gridName: `${gridName}SubAccount`,
            });
          }
          return local.value || '-';
        },
      }));
    }
    this.columns = this.columns.concat(conversionColumns('Metrics/'));

    const items = [
      {
        type: 'button',
        id: 'button-download',
      }, {
        type: 'dropdown',
        id: 'dropdown-column-chooser',
      },
    ];

    if (this.gridName !== 'HotelSubAccountGrid') {
      const bulkEditDropdown = {
        type: 'dropdown',
        button: {
          text: i18nShared.getString('HotelCenter_Shared_Bulk_Edit'),
        },
        id: 'bulk-edit-dropdown',
        menu: {
          items: [{
            text: i18n.getString('HotelCenter_Overview_Edit_Change_Bids_Label'),
            id: 'bulk-edit-change-bids',
            onClick: () => onBulkEditChangeBids(this.gridToolbar),
          }, {
            text: i18n.getString('HotelCenter_Overview_Edit_Increase_Decrease_Bids_Label'),
            id: 'bulk-edit-increase-decrease-bids',
            onClick: () => onBulkEditIncreseOrDecreseBids(this.gridToolbar),
          }, {
            type: 'dropdown-divider',
          }, {
            id: 'bulk-edit-change-multipliers-bids',
            text: i18n.getString('HotelCenter_Overview_Edit_Change_Multipliers_Bids_Label'),
            onClick: () => onBulkEditChangeMultipliersBids(this.gridToolbar),
          }],
        },
      };

      items.unshift(bulkEditDropdown);
    }

    this.gridToolbar = new OverridableToolbarView({
      toolbarId: _.uniqueId('grid-toolbar-'),
      items,
    }).render();
    $('.hotel-center-global-toolbar-containter .global-toolbar-container').append(this.gridToolbar.$el);

    this.plugins = {
      performanceLogNew: {
        instrumentation,
      },
      pagination: {
        pageSize: 20,
        availablePageSizes: pageSizes,
        enableRandomPage: false,
        pageSizeText: this.i18n.getString('Grid_ShowRows'),
        totalPageCountText: this.i18n.getString('Grid_PageOf'),
      },
      dateFilter: {
        container: dateFilterContainer,
        isComparisonEnabled: false,
        isCustomRangeEnabled: true,
      },
      inlineDownload: {
        isForVNext: true,
        i18n,
        DownloadView,
        isAvailable: true,
        gridId: gridName,
        toolbar: undefined,
        s$i18n,
        p$daterange,
        schema,
        downloadFormats: ['Csv', 'Csvzip'],
        processReportRule: ReportRule => _.defaults({
          Selection: _.defaults({
            CampaignId: _.contains(['HotelGroup', 'Hotel'], schema.name) && state.get('subAccount_id') ?
              Number(state.get('subAccount_id')) :
              undefined,
            AdGroupId: schema.name === 'Hotel' && state.get('hotelGroup_drillDown') ?
              Number(state.get('hotelGroup_drillDown').hotelGroupId) :
              undefined,
          }, ReportRule.Selection),
        }, ReportRule),
        ReportEmailRule: (() => {
          const ReportEmailRule = state.get('$ReportEmailRule');
          state.unset('$ReportEmailRule');
          return _.isObject(ReportEmailRule) ? ReportEmailRule : null;
        })(),
      },
      uiBlocker: {
        el: $('.hotel-center-overview-page'),
      },
      editable: {
        i18n: this.i18n,
      },
      columnChooser: getColumnChooserConfig({
        attributesColumns: this.attributesColumns,
        performanceColumns: this.performanceColumns,
      }),
    };

    this.rows = {
      headRows: [
        crossPageSelectHeadRow(),
        'column-header-rows',
      ],
    };
  }

  renderPopupEditor({
    newPosition, editorView, className,
  }) {
    const popupEditor = new GridCustomizedPopupEditor({
      editorView,
      cellPosition: newPosition,
      className,
      tableContainer: tableContainerSelector,
      parentContainerSelector: '.grid-and-disclaim-container',
      renderBelow: true,
    }).render();

    popupEditor.on('close', (options) => {
      if (options && options.refreshForGrid) {
        this.view.grid.gridView.refresh(true);
      }
      if (options && options.refreshForSubAccountBar) {
        this.view.grid.gridView.trigger('subAccountBarChanged');
      }
    });

    triggerScrollbarRepositionOnElementHeightChange(popupEditor.el);
  }

  bidPrompt({ position, newPosition, item }, serverContext) {
    const bidEditorPromise = import(/* webpackMode: 'eager' */'../inline-edit/bid-inline-edit-view');
    let bidEditor;
    const currentSubAccountId = state.get('subAccount_id');

    switch (this.gridName) {
      case 'HotelSubAccountGrid':
        bidEditorPromise.then(({ SubAccountBidInlineEditView }) => {
          bidEditor = new SubAccountBidInlineEditView({
            selectedItem: item,
            currentSubAccountId,
            serverContext,
            i18n: this.i18n,
          });
          this.renderPopupEditor({
            position, newPosition, editorView: bidEditor, className: 'grid-popupeditor bid-inline-edit-popupeditor',
          });
        });
        break;
      case 'HotelIdGrid':
        bidEditorPromise.then(({ HotelBidInlineEditView }) => {
          bidEditor = new HotelBidInlineEditView({ selectedItem: item, serverContext });
          this.renderPopupEditor({
            position, newPosition, editorView: bidEditor, className: 'grid-popupeditor bid-inline-edit-popupeditor',
          });
        });
        break;
      case 'HotelGroupGrid':
        bidEditorPromise.then(({ HotelGroupBidInlineEditView }) => {
          bidEditor = new HotelGroupBidInlineEditView({ selectedItem: item, serverContext });
          this.renderPopupEditor({
            position, newPosition, editorView: bidEditor, className: 'grid-popupeditor bid-inline-edit-popupeditor',
          });
        });
        break;
      default: break;
    }
  }

  bidMultipliersPrompt({ newPosition, item }, serverContext) {
    let bidMultipliersEditor;
    const bidMultipliersEditorPromise = import(/* webpackMode: 'eager' */'../inline-edit/multiplier-inline-edit-view');
    const currentSubAccountId = state.get('subAccount_id');

    switch (this.gridName) {
      case 'HotelSubAccountGrid':
        bidMultipliersEditorPromise.then(({ SubAccountBidMultipliersInlineEditView }) => {
          bidMultipliersEditor =
            new SubAccountBidMultipliersInlineEditView({
              selectedItem: item,
              currentSubAccountId,
              serverContext,
            });
          this.renderPopupEditor({
            newPosition, editorView: bidMultipliersEditor, className: 'grid-popupeditor bid-multi-inline-edit-popupeditor',
          });
        });

        break;
      case 'HotelIdGrid':
        bidMultipliersEditorPromise.then(({ HotelBidMultipliersInlineEditView }) => {
          bidMultipliersEditor = new HotelBidMultipliersInlineEditView({
            selectedItem: item,
            serverContext,
          });
          this.renderPopupEditor({
            newPosition, editorView: bidMultipliersEditor, className: 'grid-popupeditor bid-multi-inline-edit-popupeditor',
          });
        });
        break;
      case 'HotelGroupGrid':
        bidMultipliersEditorPromise.then(({ HotelGroupBidMultipliersInlineEditView }) => {
          bidMultipliersEditor = new HotelGroupBidMultipliersInlineEditView({
            selectedItem: item,
            serverContext,
          });
          this.renderPopupEditor({
            newPosition, editorView: bidMultipliersEditor, className: 'grid-popupeditor bid-multi-inline-edit-popupeditor',
          });
        });
        break;
      default: break;
    }
  }

  namePrompt({
    newPosition, item, event,
  }) {
    if (include(event.target.className, 'drillDownLink')) {
      return;
    }
    inlinePopupEditor({
      position: newPosition,
      schema: this.schema,
      model: item,
      fields: [{
        property: 'Name',
      }],
      hasLabels: false,
      template: 'inline',
      saveModel: (updated, editorView) => this.inlineEditSubAccountName(updated, editorView),
      i18n,
      parentContainerSelector: '.grid-and-disclaim-container',
    }, this);
  }

  inlineEditSubAccountName(updated, editorView) {
    const subAccountModel = new SubAccountModel({
      Id: updated.Id,
      Name: updated.Name,
    });

    subAccountModel.set('Name', updated.Name);

    return subAccountModel
      .save(['Name'], { patch: true }).then(() => {
        editorView.trigger('save', updated);
        this.view.grid.gridView.refresh(true);
        this.view.trigger('change:subAccountName');
      }).catch((e) => {
        if (e.errors) {
          const results = _.pluck(e.errors, 'Code')[0];

          _.each(results, (result) => {
            // TODO: mapping the error code
            window.App.views.alertCenter.toastError(result);
          });
        }
      }).finally(withUiBlock(this.el));
  }
}
